'use client';

import { Open, Swap } from '@drift-labs/icons';
import { UISerializableSettlePnlRecord } from '@drift/common';
import { useEffect, useMemo, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import { sortRecordsByTs } from 'src/utils/events';
import { getUrlForTx } from 'src/utils/explorer';
import TableV2 from '../Tables/TableV2';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useSettledBalancesHistory } from 'src/hooks/historyData/useSettledBalancesHistory';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { usePerpMarkets } from 'src/hooks/perpMarkets/usePerpMarkets';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import Link from 'src/components/primitives/Link/Link';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import MarketIcon from 'src/components/Utils/MarketIcon';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import Button from 'src/components/primitives/Button/Button';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const PAGE_SIZE = 20;
const EMPTY_SETTLE_PNL_ARRAY: UISerializableSettlePnlRecord[] = [];
const HEADERS = ['Time', 'Market', 'Amount settled', ''];
const TABLE_GRID = '1fr 1fr 1fr minmax(40px,.5fr)';

const V2SettlePnlHistoryTableDisplay = (
	props: HistoryTableUIProps<UISerializableSettlePnlRecord>
) => {
	const currentPerpMarkets = usePerpMarkets();
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState('settled-balances')
	);
	const currentPage = filters.currentUiPage;
	const isMobileScreenSize = useIsMobileScreenSize();

	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	// Use history data from hook instead of Zustand store
	const { historyData } = props;

	const settlePnls: UISerializableSettlePnlRecord[] =
		historyData.initialHistoryLoaded
			? historyData.records
			: EMPTY_SETTLE_PNL_ARRAY;

	const filteredSettledPnlHistory = useMemo(
		() =>
			props.filterFn
				? settlePnls?.filter((record) => props.filterFn(record, filters))
				: settlePnls,
		[settlePnls, props.filterFn, filters]
	);

	const [currentSettlePnlHistory, setCurrentSettlePnlHistory] = useState(
		filteredSettledPnlHistory?.slice(0, PAGE_SIZE) ?? undefined
	);

	const loading = historyData.loading ?? false;

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? (historyData.totalCount ?? 0) // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredSettledPnlHistory?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	useEffect(() => {
		setCurrentSettlePnlHistory(
			filteredSettledPnlHistory?.slice(
				currentPage * PAGE_SIZE,
				currentPage * PAGE_SIZE + PAGE_SIZE
			) ?? undefined
		);
	}, [filteredSettledPnlHistory, currentPage]);

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			currentPage={currentPage}
			pageChangeCallback={(newPage) =>
				props.executePageChange(newPage, PAGE_SIZE)
			}
			totalItems={maxNumOfRecordsToDisplay}
			loading={loading}
			tableState={{
				records: currentSettlePnlHistory,
				id: 'settled_pnl_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow grid={TABLE_GRID} lastColumnJustify="start">
					{HEADERS.map((label, index) => (
						<TableV2.HeaderCell key={`deposit_header_${index}`}>
							{label.toLowerCase() === 'time' ? (
								<ToggleHeaderCell
									label={label}
									onClick={toggleDateDisplayType}
								/>
							) : (
								label
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={sortRecordsByTs(currentSettlePnlHistory)?.map(
				(record, index) => {
					const market = currentPerpMarkets.find(
						(mkt) => mkt.marketIndex.toString() == record.marketIndex.toString()
					);
					const marketSymbol = market?.symbol ?? '';

					if (isMobileScreenSize) {
						return (
							<MobileTableCard.Root
								key={record.ts.toString()}
								className="gap-y-lg"
							>
								<div className="flex items-center gap-x-xs">
									<MarketIcon marketSymbol={marketSymbol} sizeClass="size-5" />
									<span className="typo-t2">{marketSymbol}</span>
								</div>

								<div className="grid w-full grid-cols-2">
									<MobileTableCard.LabelWithValue
										label="Amount settled"
										value={
											<NumberDisplay
												value={record.pnl}
												type="currency"
												isAccountValueToHide
												signDependentColor
											/>
										}
									/>
									<MobileTableCard.LabelWithValue
										className="items-end"
										label={
											<>
												Time
												<IconButton
													size="sm"
													variant="tertiary"
													icon={Swap}
													onClick={toggleDateDisplayType}
												/>
											</>
										}
										value={
											<TableV2.DateCell
												className="w-fit py-0 whitespace-nowrap"
												displayAsTimestamp={displayDatesAsTimestamp}
												date={new Date(record.ts.toNumber() * 1000)}
											/>
										}
									/>
								</div>

								<Button
									size="sm"
									variant="secondary"
									as="a"
									href={getUrlForTx(record.txSig)}
									openInNewTab
								>
									View in scanner
								</Button>
							</MobileTableCard.Root>
						);
					}

					return (
						<TableV2.BodyRow
							key={`${index}_${record.ts}`}
							grid={TABLE_GRID}
							lastColumnJustify="start"
							className="h-10 border-0"
							isDataRow
						>
							<TableV2.DateCell
								date={new Date(record.ts.toNumber() * 1000)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>

							<TableV2.MarketCell
								baseAssetSymbol={market?.baseAssetSymbol}
								key={`market_${marketSymbol}_${index}`}
								marketSymbol={marketSymbol}
							/>

							<TableV2.NotionalCell
								key={`amount_settled_${index}`}
								value={record.pnl}
								isAccountValueToHide
								signDependentColor
								disableTooltip
							/>

							<TableV2.BodyCell className="justify-end">
								<Link
									variant="secondary"
									href={getUrlForTx(record.txSig)}
									rightIcon="none"
									openInNewTab
								>
									<Open size={16} />
								</Link>
							</TableV2.BodyCell>
						</TableV2.BodyRow>
					);
				}
			)}
			tableContentBottom={
				<p>
					This includes funding fees, position P&L, and referral rewards.
					Claiming unsettled balances will update your available USDC balance
					for staking and withdrawals.{' '}
					<Link
						href="https://docs.drift.trade/profit-loss/what-is-unsettled-profit-loss"
						openInNewTab
					>
						Learn more
					</Link>
				</p>
			}
		/>
	);
};

const V2SettlePnlHistoryTable = () => {
	const tableState = useSettledBalancesHistory();

	return <V2SettlePnlHistoryTableDisplay {...tableState} />;
};

export default V2SettlePnlHistoryTable;
